import Phaser from 'phaser';
import { palette } from '../design/palette';
import { PLAY_MARGIN_X } from './Playfield';
import { LOGICAL_WIDTH } from '../dimensions';

const LEFT_X = PLAY_MARGIN_X;
const RIGHT_X = LOGICAL_WIDTH - PLAY_MARGIN_X;
export const BAR_THICKNESS = 8;

export const ANCHOR_MIN_Y = 40;
export const ANCHOR_MAX_Y = 160800;
export const ANCHOR_INITIAL_Y = 160620;

export class Bar {
  public leftAnchorY = ANCHOR_INITIAL_Y;
  public rightAnchorY = ANCHOR_INITIAL_Y;

  private body: MatterJS.BodyType;
  private gfx: Phaser.GameObjects.Graphics;

  constructor(private scene: Phaser.Scene) {
    const M = scene.matter;
    const cx = (LEFT_X + RIGHT_X) / 2;
    const cy = ANCHOR_INITIAL_Y;
    const length = RIGHT_X - LEFT_X;
    this.body = M.add.rectangle(cx, cy, length, BAR_THICKNESS, {
      isStatic: true,
      friction: 0.45,
      label: 'bar',
      chamfer: { radius: 2 }
    }) as MatterJS.BodyType;

    this.gfx = scene.add.graphics();
    this.gfx.enableFilters();
    const glow = this.gfx.filters?.internal.addGlow(palette.barGlow, 5, 0, 1, false, 10, 12);
    // Expand the filter framebuffer past the line's natural bounds so the 12px halo
    // doesn't get clipped to a hard rectangle. distance + 2 for a safety margin.
    glow?.setPaddingOverride(-14, -14, 14, 14);
    // Graphics has no intrinsic size, so Phaser auto-focus sets filtersFocusContext=true
    // (mirrors the main camera, identity-transforms the filter pass and ignores GO position).
    // Disable that so our focusFiltersOverride() call in redraw() controls the filter camera.
    this.gfx.setFiltersFocusContext(false);
    this.redraw();
  }

  public update(): void {
    const cx = (LEFT_X + RIGHT_X) / 2;
    const cy = (this.leftAnchorY + this.rightAnchorY) / 2;
    const dy = this.rightAnchorY - this.leftAnchorY;
    const dx = RIGHT_X - LEFT_X;
    const angle = Math.atan2(dy, dx);
    this.scene.matter.body.setPosition(this.body, { x: cx, y: cy });
    this.scene.matter.body.setAngle(this.body, angle);
    this.redraw();
  }

  public setAnchor(zone: 'left' | 'right', y: number): void {
    if (zone === 'left') this.leftAnchorY = y;
    else this.rightAnchorY = y;
  }

  public reset(): void {
    this.leftAnchorY = ANCHOR_INITIAL_Y;
    this.rightAnchorY = ANCHOR_INITIAL_Y;
    this.update();
  }

  public getTopYAt(x: number): number {
    const t = (x - LEFT_X) / (RIGHT_X - LEFT_X);
    return this.leftAnchorY + t * (this.rightAnchorY - this.leftAnchorY) - BAR_THICKNESS / 2;
  }

  private redraw(): void {
    // Draw in object-local coords centered on (0,0); set the GameObject's position
    // to the bar's world center. Then focusFiltersOverride tells the filter pipeline
    // to size its framebuffer to the bar's bounds and center it on the GameObject.
    // Without this override, Graphics has no intrinsic size, so the pipeline falls
    // back to filtersFocusContext mode (which ignores GO position).
    const cx = (LEFT_X + RIGHT_X) / 2;
    const cy = (this.leftAnchorY + this.rightAnchorY) / 2;
    const halfLen = (RIGHT_X - LEFT_X) / 2;
    const halfDy = (this.rightAnchorY - this.leftAnchorY) / 2;
    const length = RIGHT_X - LEFT_X;
    // Framebuffer height must accommodate the bar's vertical span when tilted, or the
    // diagonal line gets clipped to a thin horizontal strip.
    const height = Math.abs(this.rightAnchorY - this.leftAnchorY) + BAR_THICKNESS;
    this.gfx.setPosition(cx, cy);
    this.gfx.focusFiltersOverride(length / 2, height / 2, length, height);
    this.gfx.clear();
    this.gfx.lineStyle(BAR_THICKNESS, palette.barStroke, 1);
    this.gfx.beginPath();
    this.gfx.moveTo(-halfLen, -halfDy);
    this.gfx.lineTo(halfLen, halfDy);
    this.gfx.strokePath();
  }
}
